import React, { useState, useEffect } from 'react';
import { X, TrendingUp, Calendar, Star, RefreshCw, Activity, DollarSign, Users, AlertTriangle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const fmtKWD = (n) => (n == null ? '—' : `${Number(n).toFixed(3)} KWD`);
const fmtPct = (n) => (n == null ? '—' : `${n}%`);
const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString());

/**
 * Owner-only modal showing one staff member's monthly performance snapshot.
 * Per the user's iteration_23 scope decision, staff do NOT see their own
 * metrics — this component is wired into the Staff tab of the Provider
 * Dashboard only.
 */
const StaffPerformanceModal = ({ staff, onClose }) => {
  const [now] = useState(new Date());
  const [scope, setScope] = useState('month'); // 'month' | 'year'
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('sessionToken');

  useEffect(() => {
    if (!staff?.id) return;
    setLoading(true);
    setError(null);
    const url = scope === 'year'
      ? `${BACKEND_URL}/api/provider/staff/${staff.id}/performance/yearly?year=${year}`
      : `${BACKEND_URL}/api/provider/staff/${staff.id}/performance?year=${year}&month=${month}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.detail || `Request failed (${r.status})`);
        }
        return r.json();
      })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [staff?.id, scope, year, month, token]);

  // Last-12-months dropdown (monthly view)
  const monthOptions = [];
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    monthOptions.push({
      y: d.getFullYear(),
      m: d.getMonth() + 1,
      label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
    });
  }
  // Year dropdown (current + 3 previous)
  const yearOptions = [];
  for (let i = 0; i < 4; i += 1) {
    yearOptions.push(now.getFullYear() - i);
  }

  const m = data?.metrics;
  const ratingStars = m?.avg_rating ? '★'.repeat(Math.round(m.avg_rating)) + '☆'.repeat(5 - Math.round(m.avg_rating)) : '';
  const newPct = m?.new_vs_returning && m.new_vs_returning.unique_customers > 0
    ? Math.round((m.new_vs_returning.new / m.new_vs_returning.unique_customers) * 100)
    : 0;
  const returningPct = 100 - newPct;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="staff-performance-modal">
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-5 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <div>
            <h2 className="text-xl font-bold" data-testid="staff-performance-name">
              {staff?.name || data?.staff_name || 'Staff Performance'}
            </h2>
            <p className="text-blue-100 text-sm mt-0.5">Monthly performance snapshot</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-blue-500/50"
            data-testid="staff-performance-close-btn"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Scope toggle + period picker */}
          <div className="flex items-center gap-3 flex-wrap">
            <div
              className="inline-flex bg-gray-100 rounded-lg p-1 text-sm"
              data-testid="staff-performance-scope-toggle"
            >
              {[
                { v: 'month', label: 'Monthly' },
                { v: 'year', label: 'Yearly' },
              ].map((o) => (
                <button
                  key={o.v}
                  data-testid={`staff-performance-scope-${o.v}`}
                  onClick={() => setScope(o.v)}
                  className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
                    scope === o.v
                      ? 'bg-white shadow-sm text-blue-700'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            <label className="text-sm font-medium text-gray-700">Period:</label>
            {scope === 'month' ? (
              <select
                data-testid="staff-performance-month-select"
                value={`${year}-${month}`}
                onChange={(e) => {
                  const [y, mm] = e.target.value.split('-').map(Number);
                  setYear(y);
                  setMonth(mm);
                }}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
              >
                {monthOptions.map((o) => (
                  <option key={`${o.y}-${o.m}`} value={`${o.y}-${o.m}`}>{o.label}</option>
                ))}
              </select>
            ) : (
              <select
                data-testid="staff-performance-year-select"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            )}
            {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm flex items-start gap-2" data-testid="staff-performance-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && m && (
            <>
              {/* Top KPI row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard
                  label="Revenue"
                  value={fmtKWD(m.revenue)}
                  icon={<DollarSign className="w-4 h-4" />}
                  accent="text-emerald-700 bg-emerald-50 border-emerald-200"
                  testid="kpi-revenue"
                />
                <KpiCard
                  label="Appointments"
                  value={fmtNum(m.appointments)}
                  icon={<Calendar className="w-4 h-4" />}
                  accent="text-blue-700 bg-blue-50 border-blue-200"
                  testid="kpi-appointments"
                />
                <KpiCard
                  label="Avg Rating"
                  value={m.avg_rating != null ? `${m.avg_rating}` : '—'}
                  sub={m.avg_rating != null ? `${ratingStars} · ${m.review_count} reviews` : 'No reviews yet'}
                  icon={<Star className="w-4 h-4" />}
                  accent="text-amber-700 bg-amber-50 border-amber-200"
                  testid="kpi-rating"
                />
                <KpiCard
                  label="Avg Ticket"
                  value={fmtKWD(m.avg_ticket)}
                  icon={<TrendingUp className="w-4 h-4" />}
                  accent="text-violet-700 bg-violet-50 border-violet-200"
                  testid="kpi-avg-ticket"
                />
              </div>

              {/* Bottom row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div
                  className="border border-gray-200 rounded-xl p-4"
                  data-testid="kpi-rebook"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500 uppercase tracking-wide font-medium">Rebook rate</span>
                    <RefreshCw className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900">{fmtPct(m.rebook_rate)}</div>
                  <p className="text-xs text-gray-500 mt-1">
                    of {scope === 'year' ? 'this year' : 'this month'}&apos;s customers have booked with {staff?.name || 'this staff member'} again at least once.
                  </p>
                </div>

                <div
                  className="border border-gray-200 rounded-xl p-4"
                  data-testid="kpi-utilization"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500 uppercase tracking-wide font-medium">Utilization</span>
                    <Activity className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900">{fmtPct(m.utilization)}</div>
                  <p className="text-xs text-gray-500 mt-1">
                    {m.utilization == null
                      ? 'No working schedule defined — set up Days/Hours to enable this metric.'
                      : 'of scheduled slots converted into bookings.'}
                  </p>
                </div>
              </div>

              {/* New vs returning split */}
              <div className="border border-gray-200 rounded-xl p-4" data-testid="kpi-new-returning">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-sm text-gray-500 uppercase tracking-wide font-medium">Client mix</span>
                    <div className="text-xs text-gray-400">
                      {m.new_vs_returning?.unique_customers || 0} unique customers {scope === 'year' ? 'this year' : 'this month'}
                    </div>
                  </div>
                  <Users className="w-4 h-4 text-gray-400" />
                </div>

                {m.new_vs_returning?.unique_customers > 0 ? (
                  <>
                    <div className="h-4 w-full bg-gray-100 rounded-full overflow-hidden flex">
                      <div className="bg-emerald-500 h-full" style={{ width: `${newPct}%` }} title={`New: ${newPct}%`} />
                      <div className="bg-blue-500 h-full" style={{ width: `${returningPct}%` }} title={`Returning: ${returningPct}%`} />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-sm">
                      <span className="text-emerald-700">
                        <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full mr-1.5" />
                        New {m.new_vs_returning.new} ({newPct}%)
                      </span>
                      <span className="text-blue-700">
                        Returning {m.new_vs_returning.returning} ({returningPct}%)
                        <span className="inline-block w-2 h-2 bg-blue-500 rounded-full ml-1.5" />
                      </span>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-400">No customers in this period.</p>
                )}
              </div>

              {/* Yearly-only — 12-month revenue + appointments trend */}
              {scope === 'year' && Array.isArray(data?.by_month) && (
                <div className="border border-gray-200 rounded-xl p-4" data-testid="kpi-by-month-chart">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-sm text-gray-500 uppercase tracking-wide font-medium">Monthly trend</span>
                      <div className="text-xs text-gray-400">Revenue per month — hover bars for detail</div>
                    </div>
                    <TrendingUp className="w-4 h-4 text-gray-400" />
                  </div>

                  {(() => {
                    const maxRev = Math.max(1, ...data.by_month.map((b) => b.revenue || 0));
                    return (
                      <div className="flex items-end gap-2 h-40 mt-2">
                        {data.by_month.map((b) => {
                          const heightPct = (b.revenue / maxRev) * 100;
                          const isEmpty = !b.revenue && !b.appointments;
                          return (
                            <div
                              key={b.month}
                              className="flex-1 flex flex-col items-center justify-end group"
                              data-testid={`staff-performance-month-bar-${b.month}`}
                              title={`${b.label}: ${b.revenue.toFixed(3)} KWD · ${b.appointments} appointments`}
                            >
                              <div className="text-[10px] text-gray-500 mb-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {b.revenue.toFixed(0)}
                              </div>
                              <div
                                className={`w-full rounded-t-md transition-all ${
                                  isEmpty
                                    ? 'bg-gray-200'
                                    : 'bg-gradient-to-t from-blue-600 to-blue-400 group-hover:from-blue-700 group-hover:to-blue-500'
                                }`}
                                style={{ height: isEmpty ? '4px' : `${Math.max(4, heightPct)}%` }}
                              />
                              <div className="text-[11px] text-gray-600 mt-1.5 font-medium">{b.label}</div>
                              <div className="text-[10px] text-gray-400">{b.appointments || ''}</div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  <div className="mt-3 text-[11px] text-gray-400 flex items-center justify-between">
                    <span>Bar height = monthly revenue (KWD). Number under label = appointments.</span>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-400 italic">
                Metrics are computed from completed and confirmed appointments. Revenue is gross
                (before commission). Reviews are matched per appointment.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, sub, icon, accent, testid }) => (
  <div className={`border rounded-xl p-3 ${accent}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-[11px] uppercase tracking-wider font-semibold opacity-80">{label}</span>
      {icon}
    </div>
    <div className="text-xl font-bold">{value}</div>
    {sub && <div className="text-[11px] opacity-70 mt-1">{sub}</div>}
  </div>
);

export default StaffPerformanceModal;
